/**
 * Safe default renderer for a slot's bound `value`. `Slot`'s `fallback`
 * prop is what a caller passes for the "no `componentId`" case — most callers pass
 * `renderPlainText(value)` as that fallback, but `Slot` itself does not call this function; it is a
 * standalone helper.
 *
 * DELIBERATELY NARROW: only the five JS primitive types safe to coerce with `String(...)` for
 * display (`string`/`number`/`bigint`/`boolean`, plus `null`/`undefined` → `null`) are handled.
 * Anything else — a plain object, an array, a `symbol`, a `function` — THROWS rather than silently
 * rendering `'[object Object]'`/`String(fn)`/etc.: a non-primitive value needs a real component
 * (`componentId`) to render safely, so a config that binds a slot's `value` to structured data
 * without also giving it a `componentId` is a bug, and this function surfaces that loudly instead of
 * papering over it with a nonsense string.
 *
 * Isolation: imports ONLY a type-only `React` (for the `React.ReactNode` return type) — no runtime
 * value, no other project_starburst import — trivially satisfying the boundary guard
 * (`../__tests__/boundary.test.ts`).
 */
import type React from 'react';

export function renderPlainText(value: unknown): React.ReactNode {
  if (value == null) return null;
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'bigint' || t === 'boolean') {
    return String(value);
  }
  throw new Error(
    'project_starburst: non-primitive slot value requires a componentId'
  );
}
